import { useEffect, useState } from 'react';
import type { VisibilityRules, PreviewMode } from '../editor/types';

interface VisibilityWrapperProps {
  rules?: VisibilityRules | null;
  previewMode?: PreviewMode;
  children: React.ReactNode;
}

/**
 * Evaluates VisibilityRules against current context (or preview overrides).
 * Returns children when visible, null when hidden.
 */
export function VisibilityWrapper({
  rules,
  previewMode,
  children,
}: VisibilityWrapperProps): React.ReactElement | null {
  const [currentDevice, setCurrentDevice] = useState<'desktop' | 'mobile'>(
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop',
  );

  useEffect(() => {
    if (previewMode) return; // Skip listener when preview overrides device

    const handleResize = () => {
      setCurrentDevice(window.innerWidth < 768 ? 'mobile' : 'desktop');
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [previewMode]);

  // No rules → always visible
  if (!rules) {
    return <>{children}</>;
  }

  // Determine effective context values
  const effectiveDevice = previewMode?.device ?? currentDevice;
  const effectiveAuthState = previewMode?.auth_state ?? getAuthState();
  const effectiveDate = previewMode?.date_override ?? new Date().toISOString();

  // Evaluate auth_state rule
  if (rules.auth_state && rules.auth_state.length > 0) {
    if (!rules.auth_state.includes(effectiveAuthState)) {
      return null;
    }
  }

  // Evaluate device rule
  if (rules.device && rules.device.length > 0) {
    if (!rules.device.includes(effectiveDevice)) {
      return null;
    }
  }

  // Evaluate geo rule — placeholder, always pass
  // rules.geo is intentionally not evaluated yet

  // Evaluate date_range rule
  if (rules.date_range) {
    const now = new Date(effectiveDate).getTime();
    const start = new Date(rules.date_range.start).getTime();
    const end = new Date(rules.date_range.end).getTime();
    if (now < start || now > end) {
      return null;
    }
  }

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Determine the user's current auth state.
 * Uses the global user state from hookstate (window.__GC_USER__ as fallback).
 * This is a simplified check — the real implementation may hook into the auth store.
 */
function getAuthState(): 'logged_out' | 'free' | 'subscriber' {
  // Safe fallback: in admin context, assume logged in free user
  // This will be refined when integrated with the auth store
  try {
    // Check for global user object that GlobalComix sets
    const gcUser = (window as unknown as Record<string, unknown>).__GC_USER__ as
      | { is_subscriber?: boolean }
      | undefined;
    if (!gcUser) return 'logged_out';
    if (gcUser.is_subscriber) return 'subscriber';
    return 'free';
  } catch {
    return 'logged_out';
  }
}
